import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Building2, Check, ChevronDown, Loader2, ShieldCheck, Star } from 'lucide-react';

import { cn } from '@/lib/utils';
import { useIsSuperadmin } from '@/hooks/admin/useIsSuperadmin';
import { useCustomer } from '@/hooks/useCustomer';
import { useMyMemberships, useSetActiveCustomer } from '@/hooks/useMemberships';

type Variant = 'header' | 'sidebar';

/**
 * Customer-account context in the sidebar. Two behaviors (Sky, 2026-07-09):
 *
 *   - STAFF (superadmins): a static "Ordering for <customer>" indicator —
 *     always visible so you always know whose account you're in, but NEVER
 *     a multi-customer list ("it will get confusing"). Changing customer
 *     happens in Settings → Quick order (linked right on the chip).
 *   - CUSTOMERS: the original dropdown over their OWN memberships (a user
 *     who manages several of their accounts still needs to switch), hidden
 *     below 2 entries.
 */
export function AccountSwitcher({ variant = 'sidebar' }: { variant?: Variant } = {}) {
  const { data: memberships, isLoading } = useMyMemberships();
  const { isSuperadmin } = useIsSuperadmin();
  const { data: ctx } = useCustomer();
  const setActive = useSetActiveCustomer();
  const [open, setOpen] = useState(false);

  const headerVariantEarly = variant === 'header';

  // Staff: static indicator, no list. Reads the PER-TAB acting context
  // (useCustomer, docs/ACTING-CONTEXT.md) so it shows whoever THIS tab is in —
  // hidden when a superadmin isn't acting as anyone.
  if (isSuperadmin) {
    const acting = ctx?.customer ?? null;
    if (!acting) return null;
    return (
      <div className={cn('relative', !headerVariantEarly && 'px-3 pt-3 pb-1')}>
        <div
          className={cn(
            headerVariantEarly
              ? 'px-3 py-1.5 rounded-lg text-white border border-white/10 bg-white/[0.06] backdrop-blur-md'
              : 'w-full rounded-lg border border-brix-border bg-brix-surface px-2.5 py-2',
          )}
        >
          <div className="flex items-center gap-2">
            <ShieldCheck
              size={16}
              strokeWidth={1.75}
              className={headerVariantEarly ? 'text-brix-accent shrink-0' : 'text-brix-primary shrink-0'}
            />
            <div className="text-left min-w-0 flex-1">
              <p
                className={cn(
                  'text-[10px] leading-none tracking-tight font-semibold',
                  headerVariantEarly ? 'opacity-60' : 'text-brix-muted',
                )}
              >
                Ordering for
              </p>
              <p
                className={cn(
                  'font-semibold leading-tight mt-0.5 truncate',
                  headerVariantEarly ? 'text-xs text-white max-w-[160px]' : 'text-xs text-brix-ink',
                )}
              >
                {acting.name}
                {!acting.active && (
                  <span className="ml-1 text-[10px] font-normal opacity-70">(deactivated)</span>
                )}
              </p>
            </div>
          </div>
          <Link
            to="/admin/quick-order"
            className={cn(
              'block mt-1.5 text-[10px] font-semibold underline',
              headerVariantEarly ? 'text-white/70 hover:text-white' : 'text-brix-primary hover:text-brix-ink',
            )}
          >
            Change customer →
          </Link>
        </div>
      </div>
    );
  }

  if (isLoading || !memberships || memberships.length < 2) return null;

  // A chain's accounting login works the WHOLE chain from its membership to
  // the MASTER, so those lead the list.
  //
  // ⚠ There can be MORE THAN ONE. This used to `.find()` a single master and
  // relabel that row "All stores", which was singular by construction — one
  // person, one chain. The moment Scott became AP on both HOMEROOM and THE
  // MELT MAIN, one chain (whichever sorted first) was renamed "All stores"
  // and pinned above the other, which still read as a plain "ap" row: the
  // same role rendered two different ways, and "all stores" of WHICH chain
  // was unanswerable. So the NAME is always the account's own name (Sky:
  // "It should just say, HOMEROOM. and THE MELT MAIN"), and covering every
  // store is said in the SUBTITLE where it belongs.
  const isAllStores = (m: { masterAccounting: boolean; isGroupMaster: boolean }) =>
    m.masterAccounting && m.isGroupMaster;
  const masters = memberships.filter(isAllStores);
  const ordered = masters.length
    ? [...masters, ...memberships.filter((m) => !isAllStores(m))]
    : memberships;

  const active = memberships.find((m) => m.isActive) ?? memberships[0]!;
  const activeIsAllStores = isAllStores(active);
  const isSuperadminContext = memberships.some((m) => m.role === 'superadmin');
  const headerVariant = variant === 'header';

  async function onSelect(customerId: string) {
    if (customerId === active.customerId) {
      setOpen(false);
      return;
    }
    try {
      await setActive.mutateAsync(customerId);
    } finally {
      setOpen(false);
    }
  }

  return (
    <div className={cn('relative', !headerVariant && 'px-3 pt-3 pb-1')}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="listbox"
        disabled={setActive.isPending}
        className={cn(
          'flex items-center gap-2 cursor-pointer transition-all duration-200 disabled:opacity-60 disabled:cursor-wait',
          headerVariant
            ? 'px-3 py-1.5 rounded-lg text-white border border-white/10 bg-white/[0.06] backdrop-blur-md hover:bg-white/[0.12] hover:border-white/15'
            : 'w-full rounded-lg border border-brix-border bg-brix-surface px-2.5 py-2 hover:border-brix-primary/40',
        )}
      >
        <Building2
          size={16}
          strokeWidth={1.75}
          className={headerVariant ? 'text-brix-accent shrink-0' : 'text-brix-primary shrink-0'}
        />
        <div className="text-left min-w-0 flex-1">
          <p
            className={cn(
              'text-[10px] leading-none tracking-tight font-semibold',
              headerVariant ? 'opacity-60' : 'text-brix-muted',
            )}
          >
            {isSuperadminContext
              ? 'Customer Account · acting as'
              : 'Customer Account'}
          </p>
          <p
            className={cn(
              'font-semibold leading-tight mt-0.5 truncate',
              headerVariant ? 'text-xs text-white max-w-[160px]' : 'text-xs text-brix-ink',
            )}
          >
            {active.customerName}
          </p>
          {activeIsAllStores && (
            <p
              className={cn(
                'text-[10px] leading-none truncate',
                headerVariant ? 'text-white/60' : 'text-brix-muted',
              )}
            >
              All stores
            </p>
          )}
        </div>
        {setActive.isPending ? (
          <Loader2 size={13} className="animate-spin shrink-0 opacity-70" />
        ) : (
          <ChevronDown
            size={13}
            className={cn('shrink-0 transition-transform duration-200', headerVariant ? 'text-white/70' : 'text-brix-muted')}
            style={{ transform: open ? 'rotate(180deg)' : 'none' }}
          />
        )}
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" aria-hidden onClick={() => setOpen(false)} />
          <div
            className={cn(
              'absolute z-50 rounded-xl border border-brix-border bg-white shadow-card-deep max-h-[28rem] overflow-y-auto scrollbar-thin',
              headerVariant ? 'top-full mt-2 right-0 w-72' : 'top-full mt-1 left-3 right-3',
            )}
            role="listbox"
          >
            <div className="px-3 py-2 text-[10px] tracking-tight text-brix-muted font-semibold border-b border-brix-border bg-brix-surface-alt/50 flex items-center gap-1.5">
              {isSuperadminContext ? (
                <>
                  <ShieldCheck size={11} className="text-brix-primary" />
                  Customer Account — pick one to act as
                </>
              ) : masters.length ? (
                <>Everything, or one store</>
              ) : (
                <>Customer Account</>
              )}
            </div>
            {ordered.map((m) => (
              <button
                key={m.customerId}
                type="button"
                role="option"
                aria-selected={m.isActive}
                onClick={() => void onSelect(m.customerId)}
                className="w-full text-left px-3 py-2 text-sm hover:bg-brix-primary-tint flex items-center justify-between transition-colors duration-150 cursor-pointer"
              >
                <span className="min-w-0 flex-1">
                  <span className="block font-medium text-brix-ink truncate">
                    {m.isFavorite && (
                      <Star
                        size={11}
                        className="inline-block mr-1 -mt-0.5 text-brix-accent fill-brix-accent"
                      />
                    )}
                    {m.customerName}
                    {!m.customerActive && (
                      <span className="ml-1 text-brix-muted text-[10px]">(deactivated)</span>
                    )}
                  </span>
                  <span className="block text-[10px] text-brix-muted capitalize">
                    {isAllStores(m) ? 'All stores' : m.role.replace('_', ' ')}
                  </span>
                </span>
                {m.isActive && <Check size={14} className="text-brix-primary shrink-0" />}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
